import { ApiKeyOrGetter } from "./GeneralService";
import { GeneralService } from "./GeneralService";

export class DatasetService {
  public static getDatasets(
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByIntentGroup(
    intentGroupId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/intent_group/${intentGroupId}/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByIntent(
    applicationId: number,
    intentId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/application/${applicationId}/${intentId}/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetsByTestSet(
    testSetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/test_set/${testSetId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static createDataset(
    payload: FormData,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/datasets/",
      payload,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey,
      true
    );
  }

  public static validateS3Source(
    payload: { s3_url: string; region?: string; file_glob?: string },
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      "/datasets/s3_validate",
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getS3SyncerInfo(
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      "/datasets/s3_syncer_info",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static cancelS3Import(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/s3_cancel`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateDataset(
    datasetId: number,
    payload: any,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.delete(
      `/datasets/${datasetId}`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetRequests(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter,
    requestTypes?: string[]
  ) {
    let url = `/datasets/${datasetId}/requests/${offset}/${limit}`;

    if (requestTypes && requestTypes.length > 0) {
      const params = requestTypes.map((type) => `request_type[]=${type}`);
      url += `?${params.join("&")}`;
    }

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getEligibleDatasetRequests(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter,
    sessionId?: string,
    requestTypes?: string[]
  ) {
    let url = `/datasets/${datasetId}/eligible_requests/${offset}/${limit}`;
    const params: string[] = [];

    if (sessionId) {
      params.push(`session_id=${sessionId}`);
    }
    if (requestTypes && requestTypes.length > 0) {
      requestTypes.forEach((type) => params.push(`request_type[]=${type}`));
    }

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static searchEligibleDatasetRequests(
    datasetId: number,
    query: string,
    keywords: string[],
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/eligible_requests/search`,
      JSON.stringify({ query, keywords }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static searchAddedDatasetRequests(
    datasetId: number,
    query: string,
    keywords: string[],
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/added_requests/search`,
      JSON.stringify({ query, keywords }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static addDatasetRequests(
    datasetId: number,
    payload: any,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/requests/add`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static removeDatasetRequests(
    datasetId: number,
    payload: any,
    loadingHandler: any,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/requests/remove`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getEstimatedDatasetRequestCount(
    intentGroupId: number | null,
    applicationId: number | null,
    intentId: number | null,
    dateStart: number | null,
    dateEnd: number | null,
    ignoredApplicationIds: number[] | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    let url = "/datasets/estimated_request_count?";

    if (intentGroupId) {
      url += `intent_group_id=${intentGroupId}&`;
    }

    if (applicationId) {
      url += `application_id=${applicationId}&`;
    }

    if (intentId) {
      url += `intent_id=${intentId}&`;
    }

    if (dateStart) {
      url += `date_start=${dateStart}&`;
    }

    if (dateEnd) {
      url += `date_end=${dateEnd}&`;
    }
    if (ignoredApplicationIds && ignoredApplicationIds.length > 0) {
      url += `ignored_application_ids=${ignoredApplicationIds.join(",")}&`;
    }

    // Remove trailing '&' if present
    url = url.endsWith("&") ? url.slice(0, -1) : url;

    return GeneralService.get(
      url,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getIntentGroupRequestCount(
    intentGroupId: number,
    dateStart: number | null,
    dateEnd: number | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/count/intent_group/${intentGroupId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getApplicationIntentRequestCount(
    applicationId: number,
    intentId: number,
    dateStart: number | null,
    dateEnd: number | null,
    loadingHandler: ((val: boolean) => void) | undefined,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/count/application/${applicationId}/intent/${intentId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetRequestDistribution(
    datasetId: string | number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/request_distribution`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static updateDatasetRequestTags(
    datasetId: string | number,
    requestId: string,
    tags: string[],
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/datasets/${datasetId}/request/${requestId}/tags`,
      JSON.stringify({ tags }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getRegexReplacementsApplicableCount(
    datasetId: number,
    pattern: string,
    replacement: string,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/modification/regex_replacements/applicable_count`,
      JSON.stringify({ pattern, replacement }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static applyRegexReplacements(
    datasetId: number,
    regexReplacements: any[],
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/modification/regex_replacements`,
      JSON.stringify({ regex_replacements: regexReplacements }),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static cloneDataset(
    datasetId: number,
    payload: any,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/datasets/${datasetId}/clone`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static startAiReview(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter,
    payload: {
      review_type: string;
      instructions?: string;
      confidence_threshold?: number;
    }
  ) {
    return GeneralService.post(
      `/dataset_review/dataset/${datasetId}/review`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviewStatus(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_review/dataset/${datasetId}/status`,
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      apiKey
    );
  }

  public static augmentRequest(
    requestId: string,
    count: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter,
    datasetId: number
  ) {
    return GeneralService.post(
      `/dataset_augmentation/request/${requestId}/augment`,
      JSON.stringify({ count, dataset_id: datasetId }),
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviews(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_review/dataset/${datasetId}/reviews/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviewItems(
    reviewId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_review/review/${reviewId}/items/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getPublicReviewTask(
    publicToken: string,
    loadingHandler: ((val: boolean) => void) | null
  ) {
    return GeneralService.get(
      `/dataset_review/public/review/${publicToken}/next`,
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      ""
    );
  }

  public static submitPublicReview(
    publicToken: string,
    payload: {
      request_id: string;
      result: string;
      correction?: string;
    },
    loadingHandler: ((val: boolean) => void) | null
  ) {
    return GeneralService.post(
      `/dataset_review/public/review/${publicToken}/submit`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      ""
    );
  }

  public static getDatasetReview(
    reviewId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_review/review/${reviewId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static retryDatasetReview(
    reviewId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.put(
      `/dataset_review/review/${reviewId}/retry`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static forceProlificReviewSync(
    reviewId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/dataset_review/review/${reviewId}/prolific-sync`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviewProgressByDataset(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_review/dataset/${datasetId}/progress`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetAugmentations(
    datasetId: number,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_augmentation/dataset/${datasetId}/augmentations/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetPrompt(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/prompt`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static distillReviewInstructions(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/dataset_review/dataset/${datasetId}/review/instructions`,
      JSON.stringify({}),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetReviewEstimate(
    datasetId: number,
    payload: { review_type: string },
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.post(
      `/dataset_review/dataset/${datasetId}/review/estimate`,
      JSON.stringify(payload),
      loadingHandler,
      JSON,
      GeneralService.PROD_WEST_ENDPOINT,
      apiKey
    );
  }

  public static getAugmentationRequests(
    augmentationId: string,
    offset: number,
    limit: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_augmentation/augmentation/${augmentationId}/requests/${offset}/${limit}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getAugmentationDetails(
    augmentationId: string,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/dataset_augmentation/augmentation/${augmentationId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getDatasetVersions(
    datasetId: number,
    loadingHandler: ((val: boolean) => void) | null,
    apiKey: ApiKeyOrGetter
  ) {
    return GeneralService.get(
      `/datasets/${datasetId}/versions`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
